import React, { useCallback, useMemo } from 'react';
import { DropdownItem, DropdownMenu, DropdownToggle, UncontrolledDropdown } from 'reactstrap';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { faUserCircle } from '@fortawesome/pro-light-svg-icons/faUserCircle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SerializedAccountForList } from 'fym-common/src/db/serializersTypes';
import { TypedMemo } from '../../common/types';
import { useGetAccountsQuery } from '../../../redux/api/fymAPI';
import { useAccountId, useUserToken, useUserTokenDecoded } from '../../../redux/slices/appInfo/hooks';
import { setAccountId } from '../../../redux/slices/appInfo/slice';
import { UserAvatar } from '../../common/views/userAvatar/UserAvatar';
import { useOnLogout } from '../../../redux/slices/appInfo/useOnLogout';
import { SMLRoutes } from '../../../routing/sml/SMLRoutes';
import { useSMLRouter } from '../../../routing/sml/hooks';
import { isDemoAccount } from '../../accountSelector/demoAccounts';

interface AccountSelectorButtonProps {}

interface AccountSelectorButtonItemProps {
  account: SerializedAccountForList;
  selected: boolean;
}

const AccountSelectorButtonItem: React.FC<AccountSelectorButtonItemProps> = ({ account, selected }) => {
  const dispatch = useDispatch();
  const router = useSMLRouter();

  const onClick = useCallback(() => {
    router.push(SMLRoutes.smartlinks);
    dispatch(setAccountId({ accountId: account.id }));
  }, [account.id, dispatch, router]);

  return (
    <DropdownItem disabled={selected} onClick={onClick}>
      <div className="d-flex align-items-center">
        <UserAvatar size={25} url={account.avatarProcessedUrl} name={account.name} className="me-2" />
        {account.name}
      </div>
    </DropdownItem>
  );
};

const AccountSelectorButtonComponent: React.FC<AccountSelectorButtonProps> = () => {
  const intl = useIntl();
  const userToken = useUserToken();
  const userTokenDecoded = useUserTokenDecoded();
  const accountId = useAccountId();
  const onLogout = useOnLogout(userToken);
  const { accounts, isLoading: isAccountsLoading } = useGetAccountsQuery(undefined, {
    skip: !userToken,
    selectFromResult: ({ data, isLoading, error }) => {
      return {
        accounts: data?.accounts,
        isLoading,
        error,
      };
    },
  });

  const selectedAccount = useMemo(
    () => (accountId !== null ? accounts?.find((a) => a.id === accountId) : undefined),
    [accountId, accounts]
  );
  // The old navbar menu leaves the demo artist out; the artist switcher shows it with its tag.
  const accountsWithoutCurrentOne = useMemo(
    () => accounts?.filter((a) => a.id !== accountId && !isDemoAccount(a)),
    [accountId, accounts]
  );

  if (isAccountsLoading) {
    return null;
  }

  return (
    <UncontrolledDropdown nav inNavbar>
      <DropdownToggle color="primary" outline title={userTokenDecoded?.email} aria-label={userTokenDecoded?.email}>
        {selectedAccount && userTokenDecoded ? (
          // On a phone the bar shows the icon only: the full email took the whole row (QA mob-3).
          <div className="d-flex align-items-center">
            <FontAwesomeIcon icon={faUserCircle} className="me-sm-2" />
            <span className="d-none d-sm-inline">{userTokenDecoded.email}</span>
          </div>
        ) : (
          intl.formatMessage({
            id: 'web.nav.smartlinks.accountSelector',
            defaultMessage: 'Account',
          })
        )}
      </DropdownToggle>
      <DropdownMenu end>
        {accountsWithoutCurrentOne && accountsWithoutCurrentOne.length > 0 && (
          <DropdownItem header>
            {intl.formatMessage({
              id: 'web.nav.smartlinks.switch-profile',
              defaultMessage: 'Switch Profile',
            })}
            :
          </DropdownItem>
        )}
        {accountsWithoutCurrentOne?.map((account) => {
          return <AccountSelectorButtonItem key={account.id} account={account} selected={account.id === accountId} />;
        })}
        {accountsWithoutCurrentOne && accountsWithoutCurrentOne.length > 0 && <DropdownItem divider />}
        <DropdownItem onClick={onLogout}>
          {intl.formatMessage({
            id: 'web.nav.smartlinks.logout',
            defaultMessage: 'Log Out',
          })}
        </DropdownItem>
      </DropdownMenu>
    </UncontrolledDropdown>
  );
};

export const AccountSelectorButton = TypedMemo(AccountSelectorButtonComponent);
